import { parsePhoneNumber } from 'libphonenumber-js';
import { LANGUAGE_OPTIONS } from '../shared/constants';
import { MISSING_VALUE } from './constants';

export const formatBirthDate = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString('fr-FR');
};

export const unwrapLanguage = (
  language: any
): { id?: number; name?: string; code?: string } | null => {
  if (language == null || language === '') {
    return null;
  }
  if (typeof language !== 'object') {
    const id = Number(language);
    return id ? { id } : null;
  }
  const node = language.data && language.id == null ? language.data : language;
  const entity = Array.isArray(node) ? node[0] : node;
  if (!entity) {
    return null;
  }
  const attrs = entity.attributes || {};
  const id = Number(entity.id ?? attrs.id);
  const name = entity.name || attrs.name;
  const code = entity.code || attrs.code;
  if (!id && !name && !code) {
    return null;
  }
  return {
    id: id || undefined,
    name,
    code,
  };
};

export const formatLanguage = (language: any) => {
  const unwrapped = unwrapLanguage(language);
  if (!unwrapped) {
    return null;
  }
  const fromOptions = LANGUAGE_OPTIONS.find(option => option.id === unwrapped.id);
  if (fromOptions) {
    return fromOptions.label;
  }
  if (unwrapped.code === 'en' || String(unwrapped.name).toLowerCase() === 'english') {
    return 'Anglais';
  }
  if (unwrapped.code === 'fr' || String(unwrapped.name).toLowerCase() === 'français') {
    return 'Français';
  }
  return unwrapped.name || null;
};

export const formatPhone = (phone?: string | null) => {
  if (!phone) {
    return null;
  }
  try {
    const parsed = parsePhoneNumber(phone);
    if (!parsed) {
      return { display: phone, country: null as string | null };
    }
    return {
      display: parsed.formatInternational(),
      country: parsed.country || null,
    };
  } catch {
    return { display: phone, country: null as string | null };
  }
};

export const displayOrMissing = (value?: string | null) => {
  if (value == null || String(value).trim() === '') {
    return { text: MISSING_VALUE, missing: true };
  }
  return { text: String(value).trim(), missing: false };
};

export const getServiceName = (service: any) =>
  service?.name || service?.attributes?.name || `Service ${service?.id || ''}`;

const unwrapRelation = (value: any): any[] => {
  if (value == null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (value?.data != null) {
    return Array.isArray(value.data) ? value.data : [value.data];
  }
  return [value];
};

/** Merge primary `service` + multi `services` (same logic as the beneficiaries table). */
export const getUserPrestations = (user: any) => {
  const byId = new Map<number, any>();

  const addService = (raw: any) => {
    unwrapRelation(raw).forEach((service) => {
      if (service == null) {
        return;
      }
      const id = typeof service === 'object' ? Number(service.id) : Number(service);
      if (!id || Number.isNaN(id)) {
        return;
      }
      if (!byId.has(id)) {
        byId.set(
          id,
          typeof service === 'object'
            ? { ...service, id, name: getServiceName(service) }
            : { id, name: `Service ${id}` }
        );
      }
    });
  };

  addService(user?.service);
  addService(user?.services);
  return Array.from(byId.values());
};

export const getAttachmentAdminName = (user: any) => {
  const person = unwrapPerson(user?.upper_hierarchy);
  return personName(person);
};

const unwrapPerson = (raw: any): { id?: number; firstname?: string; lastname?: string } | null => {
  if (raw == null || raw === '') {
    return null;
  }
  if (typeof raw === 'number' || typeof raw === 'string') {
    const id = Number(raw);
    return id ? { id } : null;
  }
  const node = raw.data != null && raw.id == null ? raw.data : raw;
  const entity = Array.isArray(node) ? node[0] : node;
  if (!entity) {
    return null;
  }
  const attrs = entity.attributes || {};
  const id = Number(entity.id ?? attrs.id);
  return {
    id: id || undefined,
    firstname: entity.firstname || attrs.firstname || '',
    lastname: entity.lastname || attrs.lastname || '',
  };
};

const personName = (person: { firstname?: string; lastname?: string } | null | undefined) =>
  `${person?.firstname || ''} ${person?.lastname || ''}`.trim();

const indexKnownConsultants = (user: any) => {
  const byId = new Map<number, { id: number; firstname: string; lastname: string }>();
  const add = (raw: any) => {
    unwrapRelation(raw).forEach((item: any) => {
      const person = unwrapPerson(item);
      if (!person?.id) {
        return;
      }
      const current = byId.get(person.id);
      byId.set(person.id, {
        id: person.id,
        firstname: person.firstname || current?.firstname || '',
        lastname: person.lastname || current?.lastname || '',
      });
    });
  };
  add(user?.upper_hierarchy);
  add(user?.upper_hierarchies);
  unwrapRelation(user?.service_assignments).forEach((raw: any) => {
    const source = raw?.attributes ? { ...raw.attributes } : raw;
    add(source?.upper_hierarchy);
  });
  return byId;
};

const resolveConsultantName = (
  person: { id?: number; firstname?: string; lastname?: string } | null,
  known: Map<number, { firstname: string; lastname: string }>
) => {
  if (!person) {
    return '';
  }
  const fromPerson = personName(person);
  if (fromPerson) {
    return fromPerson;
  }
  if (person.id && known.has(person.id)) {
    return personName(known.get(person.id));
  }
  return '';
};

/** Consultant id owning a prestation, from the journal then the attachment admin. */
export const getConsultantIdForService = (user: any, serviceId: number | string): number | null => {
  const targetId = Number(serviceId);
  const fallbackId = unwrapPerson(user?.upper_hierarchy)?.id || null;
  if (!targetId) {
    return fallbackId;
  }

  const assignment = unwrapRelation(user?.service_assignments).find((raw: any) => {
    const source = raw?.attributes ? { id: raw.id, ...raw.attributes } : raw;
    const serviceRaw = source?.service?.data ?? source?.service;
    const serviceEntity = Array.isArray(serviceRaw) ? serviceRaw[0] : serviceRaw;
    const id = Number(serviceEntity?.id ?? serviceEntity);
    return id === targetId;
  });
  if (assignment) {
    const source = assignment?.attributes ? { ...assignment.attributes } : assignment;
    const id = unwrapPerson(source?.upper_hierarchy)?.id;
    if (id) {
      return id;
    }
  }

  const mapEntry = user?.service_resources_map?.[String(targetId)];
  const mapConsultantId = Number(
    typeof mapEntry?.upper_hierarchy === 'object'
      ? mapEntry?.upper_hierarchy?.id
      : mapEntry?.upper_hierarchy
  );
  if (Number.isFinite(mapConsultantId) && mapConsultantId > 0) {
    return mapConsultantId;
  }

  return fallbackId;
};

/** Consultant displayed for a prestation, read from the journal then fallbacks. */
export const getConsultantDisplayForService = (user: any, serviceId: number | string) => {
  const targetId = Number(serviceId);
  if (!targetId) {
    return getAttachmentAdminName(user);
  }
  const known = indexKnownConsultants(user);

  const assignment = unwrapRelation(user?.service_assignments).find((raw: any) => {
    const source = raw?.attributes ? { id: raw.id, ...raw.attributes } : raw;
    const serviceRaw = source?.service?.data ?? source?.service;
    const serviceEntity = Array.isArray(serviceRaw) ? serviceRaw[0] : serviceRaw;
    const id = Number(serviceEntity?.id ?? serviceEntity);
    return id === targetId;
  });
  if (assignment) {
    const source = assignment?.attributes ? { ...assignment.attributes } : assignment;
    const name = resolveConsultantName(unwrapPerson(source?.upper_hierarchy), known);
    if (name) {
      return name;
    }
  }

  const mapEntry = user?.service_resources_map?.[String(targetId)];
  const mapConsultantId = Number(
    typeof mapEntry?.upper_hierarchy === 'object'
      ? mapEntry?.upper_hierarchy?.id
      : mapEntry?.upper_hierarchy
  );
  if (mapConsultantId) {
    const name = resolveConsultantName({ id: mapConsultantId }, known);
    if (name) {
      return name;
    }
  }

  return getAttachmentAdminName(user);
};

export const toDateInputValue = (value?: string | null) => {
  if (!value) {
    return '';
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const toLanguageId = (language: any): number | '' => {
  const id = Number(unwrapLanguage(language)?.id);
  return id || '';
};

export const toLanguagePayload = (language: number | '' | null) =>
  language === '' || language == null ? null : Number(language);

export type ProfileFormValues = {
  email: string;
  firstname: string;
  lastname: string;
  dayOfBirth: string;
  gender: string;
  postal_code: string;
  town: string;
  phone: string;
  language: number | '';
  diploma: string;
};

export const userToProfileForm = (user: any): ProfileFormValues => ({
  email: user?.email || '',
  firstname: user?.firstname || '',
  lastname: user?.lastname || '',
  dayOfBirth: toDateInputValue(user?.dayOfBirth),
  gender: user?.gender || '',
  postal_code: user?.postal_code || '',
  town: user?.town || '',
  phone: user?.phone || '',
  language: toLanguageId(user?.language),
  diploma: user?.diploma || '',
});
